<template>
  <div
    class="w-full pl-5 cursor-pointer"
    @mouseenter="onHoverStart"
    @mouseleave="onHoverEnd"
  >
    <div
      class="shadow-custom cursor-pointer rounded-md flex-row flex items-center border-2 border-transparent hover:border-primary/25"
      :class="{'card-shake': shaking}"
      @animationend.self="shaking = false"
    >
      <div class="relative flex flex-col flex-1 bg-gray-100 items-center">
        <div class="cursor-pointer min-w-full" @click.stop="$emit('show-program', program.id)">
          <div class="relative z-1 overflow-hidden bg-white py-2 rounded">
            <div class="pl-9 flex flex-row items-center text-sm h-20 justify-between gap-2">
              <ProgramTeaserPoll
                v-if="hasPendingPoll"
                :poll="teaserPoll!"
                :program="program"
                :consumer="consumer"
                @answered="onPollAnswered"
                @hidden="$emit('hidden')"
                @show-program="$emit('show-program', program.id)"
              />
              <ProgramTeaser
                v-else
                :program="program"
                :ellipsis="true"
              />
              <div class="flex justify-end flex-shrink-0">
                <ChevronRight class="w-6 text-gray-300 mx-2" />
              </div>
            </div>
          </div>
          <ProgramLogo :program="program" :size="50" class="absolute -left-[25px] top-5 z-2" />
        </div>
        <!-- Kept mounted while folded: the feedback's dialogs live in there,
             and must outlast the cursor leaving the card to answer them. -->
        <Collapsible
          v-if="isSupportProgram"
          :open="state.isHovered"
          :unmount-on-hide="false"
          class="z-0 pr-4 w-full"
        >
          <CollapsibleContent>
            <notification-program-feedback
              class="text-sm py-2"
              :consumer-key="consumer.key"
              :program="program"
              @hidden="$emit('hidden')"
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, reactive, ref} from "vue";
import NotificationProgramFeedback
    from "@/notification/components/notification-detection/programs/NotificationProgramFeedback.vue";
import ProgramLogo from "@/program/components/program-logo/ProgramLogo.vue";
import {ProgramSnapshot} from "@/program/interfaces/Program";
import Customer from "@/customer/interfaces/Customer";
import Consumer from "@/core/interfaces/consumers/Consumer";
import ProgramTeaser from "@/program/components/program-teaser/ProgramTeaser.vue";
import ProgramTeaserPoll from "@/program/components/program-teaser/ProgramTeaserPoll.vue";
import {ChevronRight} from "@lucide/vue";
import {Collapsible, CollapsibleContent} from "@/core/components/ui/collapsible";
import {pathwayApi} from "@/container";
import {useNotificationStore} from "@/stores/notification.store";

const props = defineProps<{
    program: ProgramSnapshot
    customer: Customer,
    consumer: Consumer
}>();

const emit = defineEmits(['resize', 'show-program', 'hidden']);

const state = reactive({
    isHovered: false,
    timeout: null as NodeJS.Timeout | null,
});

const {hiddenPolls} = useNotificationStore();

const shaking = ref(false);

const isSupportProgram = computed(() => {
    return props.program.programmable_type === 'support';
});

/** The poll displayed below the teaser card, until answered. */
const teaserPoll = computed(() => props.program.polls?.find(poll => poll.display_in_program));

/**
 * The answered flag lives in the notification store: the panel is unmounted
 * when the drawer closes, a local ref would resurrect the poll on reopen.
 */
const hasPendingPoll = computed(() => !!teaserPoll.value && !hiddenPolls[teaserPoll.value.id]);

/**
 * Swap the poll back to the regular teaser and shake the card so the
 * appearing teaser catches the eye.
 */
const onPollAnswered = () => {
    if (teaserPoll.value) {
        hiddenPolls[teaserPoll.value.id] = true;
    }

    shaking.value = true;
    emit('resize');
};

onMounted(() => {
    if (props.program.next_step && props.program.next_step.pathway_id) {
        pathwayApi().track('notification-show', props.program.next_step.pathway_id, props.consumer.key);
    }
});

/**
 * On hover start, set a timeout to display the exclusion toggler.
 */
const onHoverStart = () => {
    state.timeout = setTimeout(() => {
        state.isHovered = true;
        emit('resize');
    }, 200);
};

/**
 * On hover end, clear the timeout if overed is not true otherwise and set the hovered state to true.
 */
const onHoverEnd = () => {
    if (!state.isHovered && state.timeout) {
        clearTimeout(state.timeout);
    } else {
        state.isHovered = false;
        emit('resize');
    }
};
</script>

<style scoped>
.card-shake {
    animation: card-shake 0.5s ease-in-out;
}

@keyframes card-shake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-5px); }
    40% { transform: translateX(5px); }
    60% { transform: translateX(-3px); }
    80% { transform: translateX(3px); }
}
</style>
